import { useTranslation } from "react-i18next";
import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import { Box } from "@mui/material";
import { alphabetizePrograms, useAllPrograms } from "../Programs/programUtils";
import { useState } from "react";
import { useAppSelector } from "../../app/hooks";
import {
  selectCurrentUser,
  selectUserCognitoGroups
} from "../Users/usersSlice";
import { returnIdsOfProgramsThatUserAdmins } from "../../app/utils";
import { CognitoUserGroups } from "../Users/userUtils";
import { BackendUserReport } from "./BackendUserReport";
import { DynamicUserReport } from "./DynamicUserReport";
import { FrontendUserReports } from "./FrontendUserReport";
import { GridColDef, GridEventListener } from "@mui/x-data-grid";
import { UserReport } from "./reportUtils";
import { ProgramRoleSelectFieldsNew } from "../../app/constants";
import { ReportsDateRangePicker } from "./ReportsDateRangePicker";
import { ReportsDeprecationBanner } from "./ReportsDeprecationBanner";
import { sub } from "date-fns";
import { ReportsPageProps } from "../../app/types";
import { selectProgramsStatus } from "../Programs/programsSlice";
import {
  selectFeatureFlags,
  selectFeatureFlagsError,
  selectFeatureFlagsStatus
} from "../Firebase/firebaseSlice";

const pageDetails: PageWrapperProps = {
  className: "UserReports",
  headerText: "User Reports",
  headerVariant: "h2"
};

export const UserReports = ({ classicMode }: ReportsPageProps) => {
  const { t } = useTranslation();
  const currentUser = useAppSelector(selectCurrentUser);
  const programsLoadingStatus = useAppSelector(selectProgramsStatus);
  const currentUserPermissions = useAppSelector(selectUserCognitoGroups);
  const currentUserIsProgramAdmin = currentUserPermissions?.includes(
    CognitoUserGroups.ProgramAdmins
  );
  const programSelectionNotif =
    programsLoadingStatus === "loading"
      ? "Loading Programs..."
      : "Select Program";

  const { programs } = useAllPrograms();

  const flags = useAppSelector(selectFeatureFlags);
  const flagsStatus = useAppSelector(selectFeatureFlagsStatus);
  const flagsError = useAppSelector(selectFeatureFlagsError);

  const { use_dynamic_reports, use_backend_reports, user_ccc_reports_enabled } =
    flags;

  const idsOfProgramsThatUserAdmins =
    returnIdsOfProgramsThatUserAdmins(currentUser);
  const programsThatUserAdmins = programs?.filter((program) =>
    idsOfProgramsThatUserAdmins.includes(program.id)
  );
  const programsToDisplay = currentUserIsProgramAdmin
    ? programsThatUserAdmins
    : programs;

  const sortedPrograms = programsToDisplay
    ? alphabetizePrograms(programsToDisplay, true)
    : programs;

  const [programId, setProgramId] = useState<string | undefined>();
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [fromDate, setFromDate] = useState<Date | null>(
    sub(new Date(), { months: 1 })
  );
  const [toDate, setToDate] = useState<Date | null>(new Date());

  const handleProgramChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setProgramId(event.target.value);
  };
  const handleRoleFilterChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setRoleFilter(event.target.value);
  };

  const handleUserSpecificRowClick:
    | GridEventListener<"rowClick">
    | undefined = (rowData) => {
    const { row } = rowData;
    const { userId, programId } = row;
    if (programId && userId && user_ccc_reports_enabled) {
      window.open(`/reports/user/${userId}/${programId}`, "_blank");
    }
  };

  const handleRowClick: GridEventListener<"rowClick"> | undefined = (
    rowData
  ) => {
    const { row } = rowData;
    const { programMembershipId } = row;
    if (programMembershipId && user_ccc_reports_enabled) {
      window.open(`/reports/user/${programMembershipId}`, "_blank");
    }
  };

  const columns: GridColDef<UserReport>[] = [
    {
      field: "firstName",
      headerName: t("First Name"),
      flex: 1
    },
    {
      field: "lastName",
      headerName: t("Last Name"),
      flex: 1
    },
    {
      field: "email",
      headerName: t("Email"),
      flex: 2
    },
    {
      field: "program",
      headerName: t("Program"),
      valueGetter: (params: any) =>
        sortedPrograms?.find((programInList) => programInList.id === programId)
          ?.name,
      flex: 1
    },
    {
      field: "createdAssessmentEvents",
      headerName: t("# Created"),
      flex: 1
    },
    {
      field: "assignedAssessmentEvents",
      headerName: t("# Assigned"),
      flex: 1
    },
    {
      field: "completedAssessmentEvents",
      headerName: t("# Completed"),
      flex: 1
    },
    {
      field: "percentCompleted",
      headerName: t("% Completed"),
      flex: 1
    },
    {
      field: "programRoleString",
      headerName: t("Role"),
      flex: 1
    },
    {
      field: "expiredAssessmentEvents",
      headerName: t("# Expired"),
      flex: 1
    },
    {
      field: "percentExpired",
      headerName: t("% Expired"),
      flex: 1
    }
  ];

  const useDynamicReports = !classicMode && use_dynamic_reports;
  const useBackendReports =
    classicMode || (use_backend_reports && !use_dynamic_reports);
  const useFrontendReports = !use_dynamic_reports && !use_backend_reports;

  return (
    <PageWrapper {...pageDetails}>
      <ReportsDeprecationBanner />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          gap: 2,
          py: 1
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <label htmlFor="program-filter">{t("Program")}:</label>
          <select
            disabled={programsLoadingStatus === "loading"}
            id="program-filter"
            onChange={handleProgramChange}
          >
            <option value={undefined}>{t(programSelectionNotif)}</option>
            {sortedPrograms?.map((program) => (
              <option key={program.id} value={program.id}>{`${
                program.institution?.name || "N/A"
              } -> ${program.name || "N/A"}`}</option>
            ))}
          </select>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <label htmlFor="role-filter">{t("Role Filter")}:</label>
          <select id="role-filter" onChange={handleRoleFilterChange}>
            <option value={""}>{t("(all)")}</option>
            {ProgramRoleSelectFieldsNew.map((roleField) => (
              <option key={roleField.value} value={roleField.value}>{roleField.label}</option>
            ))}
          </select>
        </Box>
      </Box>
      {useDynamicReports && (
        <>
          <ReportsDateRangePicker
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
          />
          <DynamicUserReport
            programId={programId}
            columns={columns}
            handleRowClick={handleUserSpecificRowClick}
            roleFilter={roleFilter}
            startDate={fromDate?.toISOString()?.substring(0, 10)}
            endDate={toDate?.toISOString()?.substring(0, 10)}
          />
        </>
      )}
      {useBackendReports && (
        <BackendUserReport
          programId={programId}
          columns={columns}
          handleRowClick={handleRowClick}
          roleFilter={roleFilter}
        />
      )}
      {useFrontendReports && (
        <FrontendUserReports
          programId={programId}
          columns={columns}
          handleRowClick={handleRowClick}
          roleFilter={roleFilter}
        />
      )}
    </PageWrapper>
  );
};
